if (!customElements.get('product-modal')) {
  customElements.define(
    'product-modal',
    class ProductModal extends ModalDialog {
      constructor() {
        super();
        this.bindGalleryControls();
      }

      hide() {
        super.hide();
      }

      show(opener) {
        super.show(opener);
        this.showActiveMedia();
      }

      slider() {
        return this.querySelector('.product-media-modal__content');
      }

      items() {
        const slider = this.slider();
        return slider ? Array.from(slider.querySelectorAll(':scope > .product-media-modal__item')) : [];
      }

      // Self-contained prev/next/swipe handling for the gallery inside zoom,
      // instead of Dawn's slider-component: that component caches the pixel
      // spacing between slides once at construction time and reuses it on
      // every click, which in practice went stale and made the buttons land
      // on the wrong photo. Measuring fresh from the DOM on every interaction
      // avoids that class of bug entirely.
      bindGalleryControls() {
        const slider = this.slider();
        if (!slider) return;

        const currentIndex = () => {
          const list = this.items();
          let closest = 0;
          let closestDist = Infinity;
          list.forEach((el, i) => {
            const dist = Math.abs(el.offsetLeft - slider.scrollLeft);
            if (dist < closestDist) {
              closestDist = dist;
              closest = i;
            }
          });
          return closest;
        };

        const goTo = (index, behavior) => {
          const list = this.items();
          if (!list.length) return;
          const wrapped = (index + list.length) % list.length;
          // 'smooth' silently fails to move at all here: this track has
          // scroll-snap-type: x mandatory, and smooth scrollTo() fighting a
          // mandatory snap point is a known browser bug. 'auto' (an instant
          // jump) is the reliable option; CSS scroll-behavior below adds
          // back a smooth feel for swipes/drags without going through
          // scrollTo() at all.
          slider.scrollTo({ left: list[wrapped].offsetLeft, behavior: behavior || 'auto' });
        };

        const prevBtn = this.querySelector('.slider-button--prev');
        const nextBtn = this.querySelector('.slider-button--next');
        if (prevBtn) {
          prevBtn.addEventListener('click', (event) => {
            event.preventDefault();
            event.stopPropagation();
            goTo(currentIndex() - 1);
          });
        }
        if (nextBtn) {
          nextBtn.addEventListener('click', (event) => {
            event.preventDefault();
            event.stopPropagation();
            goTo(currentIndex() + 1);
          });
        }

        // Keep the active class + counter in sync when the user swipes or
        // drag-scrolls directly, not just when they use the arrow buttons.
        let scrollTimer;
        slider.addEventListener('scroll', () => {
          clearTimeout(scrollTimer);
          scrollTimer = setTimeout(() => this.syncActiveFromScroll(), 120);
        });
        // On a real device the counter was observed staying frozen on
        // whatever slide the modal opened on, through several swipes that
        // visibly did change the photo — the 120ms-after-scroll-settles
        // sync above apparently wasn't reliably firing for touch-driven
        // scrolling in testing. Also sync right on touchend (one frame
        // later, so the browser's own snap has resolved scrollLeft) as a
        // second, more direct trigger for the exact same gesture.
        slider.addEventListener(
          'touchend',
          () => requestAnimationFrame(() => requestAnimationFrame(() => this.syncActiveFromScroll())),
          { passive: true }
        );

        // The buttons/slider live inside the modal content; without this a
        // click or drag on them would bubble up to ModalDialog's own
        // click-on-backdrop-closes-it listener.
        this.querySelector('.product-media-modal__buttons')?.addEventListener('pointerdown', (event) =>
          event.stopPropagation()
        );
        slider.addEventListener('pointerdown', (event) => event.stopPropagation());
      }

      syncActiveFromScroll() {
        const slider = this.slider();
        if (!slider) return;
        const list = this.items();
        let closest = 0;
        let closestDist = Infinity;
        list.forEach((el, i) => {
          const dist = Math.abs(el.offsetLeft - slider.scrollLeft);
          if (dist < closestDist) {
            closestDist = dist;
            closest = i;
          }
        });
        list.forEach((el, i) => el.classList.toggle('is-active', i === closest));
        const counter = this.querySelector('.slider-counter--current');
        if (counter) counter.textContent = closest + 1;
      }

      showActiveMedia() {
        const mediaId = this.openedBy.getAttribute('data-media-id');
        const list = this.items();

        list.forEach((element) => element.classList.remove('is-active'));

        const activeMedia = this.querySelector(`.product-media-modal__item[data-media-id="${mediaId}"]`);
        if (!activeMedia) return;
        activeMedia.classList.add('is-active');

        const slider = this.slider();
        if (slider) {
          slider.scrollTo({ left: activeMedia.offsetLeft, behavior: 'auto' });
          const counter = this.querySelector('.slider-counter--current');
          const index = list.indexOf(activeMedia);
          if (counter && index > -1) counter.textContent = index + 1;
        }

        const activeMediaTemplate = activeMedia.querySelector('template');
        const activeMediaContent = activeMediaTemplate ? activeMediaTemplate.content : null;
        if (
          activeMedia.nodeName == 'DEFERRED-MEDIA' &&
          activeMediaContent &&
          activeMediaContent.querySelector('.js-youtube')
        )
          activeMedia.loadContent();
      }
    }
  );
}
